// ¿La marca activa tiene una tienda online conectada? —
// ADR-2026-10-16-tienda-online-e-instagram-para-todos.
//
// Es lo que decide si se ofrece "Publicar en tu tienda" en cada imagen: sin tienda no hay
// a dónde publicar, y el botón ocupaba el lugar de la descarga. Las secciones Tienda
// online e Instagram son de todos; este botón, sólo de quien tiene dónde publicar.
//
// "Conectada" es lo mismo que ya preguntan las métricas (`sinTiendasConectadas`) y el
// agente (`ChatAtlasContext`): Tiendanube conectada, o Mercado Libre disponible y
// conectado. Ninguna de las dos consultas tira: un error cuenta como "no conectada", que
// lo peor que hace es no ofrecer el botón (la descarga sigue al lado).
//
// UNA consulta por marca para toda la app, no una por botón: el hook lo montan la grilla,
// el visor, las tandas, el lienzo del agente y Flujos, a veces varios a la vez. El
// resultado se guarda en memoria y se descarta al cambiar de marca o al conectar o
// desconectar una tienda.

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { getTnStatus, EVENTO_CAMBIO_DE_TIENDANUBE } from '../lib/tiendaNube';
import { estadoDeMercadoLibre, EVENTO_CAMBIO_DE_MERCADOLIBRE } from '../lib/mercadoLibreConexion';

/** La regla, pura: Tiendanube conectada, o Mercado Libre disponible y conectado. */
export function hayTiendaConectada(
  tn: { connected: boolean },
  ml: { disponible: boolean; conectada: boolean },
): boolean {
  return tn.connected || (ml.disponible && ml.conectada);
}

let pedido: Promise<boolean> | null = null;
let ultimo: boolean | null = null;
/** Sube con cada `invalidar()`: un pedido de una vuelta vieja (otra marca, una tienda que ya
 *  se conectó) no publica su respuesta aunque llegue después que el nuevo. */
let vuelta = 0;
/** De qué usuario es lo sabido: otra pestaña puede entrar con otra cuenta sin que ésta se
 *  recargue (ADR-2026-10-16-la-tienda-conectada-tiene-dueno). */
let dueno: string | null = null;
const suscriptores = new Set<(v: boolean) => void>();

/** La marca activa del usuario: la misma columna que lee `useBrandProfile`. */
async function marcaActiva(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  dueno = user?.id ?? null;
  if (!user) return null;
  const { data } = await supabase
    .from('profiles')
    .select('current_brand_id')
    .eq('id', user.id)
    .single();
  return (data?.current_brand_id as string | null) ?? null;
}

export function consultar(): Promise<boolean> {
  if (pedido) return pedido;
  const esta = vuelta;
  pedido = (async () => {
    try {
      // Sin marca activa, `getTnStatus(null)` responde por la marca por defecto.
      const marca = await marcaActiva();
      const [tn, ml] = await Promise.all([getTnStatus(marca), estadoDeMercadoLibre(marca)]);
      return hayTiendaConectada(tn, ml);
    } catch {
      return false;
    }
  })().then((v) => {
    // Llegó tarde: quien esperaba esta respuesta recibe la de la vuelta vigente.
    if (esta !== vuelta) return consultar();
    ultimo = v;
    suscriptores.forEach((fn) => fn(v));
    return v;
  });
  return pedido;
}

/** Olvida lo sabido y, si alguien está mirando, vuelve a preguntar. */
export function invalidar(): void {
  vuelta += 1;
  pedido = null;
  if (suscriptores.size > 0) void consultar();
}

/**
 * Cambió el usuario de la sesión: lo sabido era de otra cuenta. Hasta saber la nueva, nadie
 * ve "Publicar en tu tienda" (mismo criterio que el store de la campana,
 * ADR-2026-09-15-el-store-de-notificaciones-tiene-dueno).
 */
export function alCambiarDeUsuario(uid: string | null): void {
  if (uid === dueno) return;
  dueno = uid;
  ultimo = null;
  suscriptores.forEach((fn) => fn(false));
  invalidar();
}

if (typeof window !== 'undefined') {
  // `INITIAL_SESSION` es el usuario que la primera consulta ya está resolviendo; el resto se
  // difiere fuera del callback para no llamar a la API de auth desde adentro.
  supabase.auth.onAuthStateChange((evento, sesion) => {
    if (evento === 'INITIAL_SESSION') return;
    const uid = sesion?.user?.id ?? null;
    setTimeout(() => alCambiarDeUsuario(uid), 0);
  });
  window.addEventListener('atlas:brand-changed', invalidar);
  window.addEventListener(EVENTO_CAMBIO_DE_MERCADOLIBRE, invalidar);
  window.addEventListener(EVENTO_CAMBIO_DE_TIENDANUBE, invalidar);
  // El popup del OAuth de Tiendanube avisa con un `postMessage` a la ventana que lo abrió
  // (el mismo aviso que escucha `ChatAtlasContext`).
  window.addEventListener('message', (e: MessageEvent) => {
    const d = e.data as { type?: string } | null;
    if (d?.type === 'tn-connected' && e.origin === window.location.origin) invalidar();
  });
}

/**
 * `true` si la marca activa tiene una tienda conectada. Mientras no se sabe devuelve el
 * último valor conocido (o `false`): mejor que el botón aparezca un instante después a
 * que aparezca y se vaya.
 */
export function useTiendaConectada(): boolean {
  const [conectada, setConectada] = useState<boolean>(ultimo ?? false);
  useEffect(() => {
    let vivo = true;
    const alCambiar = (v: boolean) => {
      if (vivo) setConectada(v);
    };
    suscriptores.add(alCambiar);
    void consultar().then(alCambiar);
    return () => {
      vivo = false;
      suscriptores.delete(alCambiar);
    };
  }, []);
  return conectada;
}
